import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Mountain, Plus, Sparkles, Trash2 } from 'lucide-react';

import { listDepartments, listEmployees } from '@/api/employees.api';
import { AiDisabledError, suggestRock } from '@/api/eos/rockAssistant.api';
import {
  archiveRock,
  createRock,
  getRocksRollup,
  listRocks,
  updateRock,
  type Rock,
  type RockStatus,
  type RockType,
} from '@/api/eos/rocks.api';
import { QuarterPicker } from '@/components/eos/QuarterPicker';
import { RockDetailDrawer } from '@/components/eos/RockDetailDrawer';
import { RockStatusBadge } from '@/components/eos/RockStatusBadge';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Tabs } from '@/components/ui/Tabs';
import { describeEosError, eosErrorCode } from '@/lib/eos/errors';
import { quarterStartOf, toIsoDate } from '@/lib/eos/quarters';
import { ROCK_STATUS_META } from '@/lib/eos/rockStatus';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * EOS Rocks — Stream B Wave 1 (ADR-023).
 *
 * Quarterly priorities, org-transparent by default: everyone sees every
 * rock; writes follow the server's matrix (manage_eos/superadmin → all,
 * owners → their own status/description, department leads → their
 * department). Client-side gating here is VISIBILITY only — the server
 * re-enforces on every request.
 */

const SELECT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

/** on_track → off_track → done → on_track */
const NEXT_STATUS: Record<RockStatus, RockStatus> = {
  on_track: 'off_track',
  off_track: 'done',
  done: 'on_track',
};

type RockTab = 'company' | 'departments' | 'mine';

export function RocksPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const myMembershipId = me?.membership.id ?? null;
  const canManageAll = me !== null && (isSuperadmin(me) || hasCapability(me, 'manage_eos'));
  const isDeptLead = me?.membership.role === 'department_lead';

  const [quarter, setQuarter] = useState(() => toIsoDate(quarterStartOf(new Date())));
  const [tab, setTab] = useState<RockTab>('company');
  const [createOpen, setCreateOpen] = useState(false);
  const [openRockId, setOpenRockId] = useState<string | null>(null);

  const rocksQuery = useQuery({
    queryKey: ['eos', 'rocks', quarter],
    queryFn: () => listRocks({ quarterStart: quarter }),
  });
  const rollupQuery = useQuery({
    queryKey: ['eos', 'rocks', 'rollup', quarter],
    queryFn: () => getRocksRollup(quarter),
  });
  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: listDepartments });
  const employeesQuery = useQuery({
    queryKey: ['employees', 'directory'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
  });

  // membershipId → display name, for owner labels on cards.
  const nameByMembership = useMemo(() => {
    const out = new Map<string, string>();
    for (const e of employeesQuery.data?.data ?? []) {
      if (e.membershipId) out.set(e.membershipId, `${e.firstName} ${e.lastName}`);
    }
    return out;
  }, [employeesQuery.data]);

  const departmentName = useMemo(() => {
    const out = new Map<string, string>();
    for (const d of departmentsQuery.data ?? []) out.set(d.id, d.name);
    return out;
  }, [departmentsQuery.data]);

  const rocks = useMemo(() => rocksQuery.data ?? [], [rocksQuery.data]);
  // Re-derived from the freshly fetched list so the drawer reflects mutations.
  const openRock = useMemo(
    () => (openRockId ? (rocks.find((r) => r.id === openRockId) ?? null) : null),
    [rocks, openRockId],
  );

  const statusCounts = useMemo(() => {
    const counts: Record<RockStatus, number> = { on_track: 0, off_track: 0, done: 0 };
    for (const row of rollupQuery.data?.byStatusType ?? []) {
      counts[row.status] += row.count;
    }
    return counts;
  }, [rollupQuery.data]);

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'rocks'] });
  }

  const statusMutation = useMutation({
    mutationFn: ({ rockId, status }: { rockId: string; status: RockStatus }) =>
      updateRock(rockId, { status }),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not update the rock status.'), { variant: 'danger' });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (rockId: string) => archiveRock(rockId),
    onSuccess: () => {
      invalidate();
      toast.show('Rock archived', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeEosError(err, 'Could not archive the rock.'), { variant: 'danger' });
    },
  });

  /** Status cycling: manage_eos/superadmin → all; owner → own; dept lead → their dept rocks. */
  function canEditRock(rock: Rock): boolean {
    if (canManageAll) return true;
    if (myMembershipId !== null && rock.ownerMembershipId === myMembershipId) return true;
    return isDeptLead && rock.type === 'department';
  }

  const visibleRocks = useMemo(() => {
    if (tab === 'company') return rocks.filter((r) => r.type === 'company');
    if (tab === 'departments') return rocks.filter((r) => r.type === 'department');
    return rocks.filter((r) => r.ownerMembershipId !== null && r.ownerMembershipId === myMembershipId);
  }, [rocks, tab, myMembershipId]);

  // Departments tab groups by department name; rocks without one land last.
  const grouped = useMemo(() => {
    if (tab !== 'departments') return null;
    const groups = new Map<string, Rock[]>();
    for (const r of visibleRocks) {
      const label = r.departmentId
        ? (departmentName.get(r.departmentId) ?? 'Unknown department')
        : 'No department';
      const list = groups.get(label) ?? [];
      list.push(r);
      groups.set(label, list);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [tab, visibleRocks, departmentName]);

  function renderRock(rock: Rock) {
    return (
      <RockCard
        key={rock.id}
        rock={rock}
        ownerName={
          rock.ownerMembership
            ? `${rock.ownerMembership.user.firstName} ${rock.ownerMembership.user.lastName}`
            : rock.ownerMembershipId
              ? (nameByMembership.get(rock.ownerMembershipId) ?? null)
              : null
        }
        departmentLabel={rock.departmentId ? (departmentName.get(rock.departmentId) ?? null) : null}
        canEdit={canEditRock(rock)}
        canArchive={
          canManageAll ||
          (rock.type === 'individual' &&
            rock.ownerMembershipId !== null &&
            rock.ownerMembershipId === myMembershipId)
        }
        onCycleStatus={() => {
          statusMutation.mutate({ rockId: rock.id, status: NEXT_STATUS[rock.status] });
        }}
        onArchive={() => {
          archiveMutation.mutate(rock.id);
        }}
        onOpen={() => {
          setOpenRockId(rock.id);
        }}
      />
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="typo-h1">Rocks</h1>
          <p className="mt-1 text-[14px] text-kw-muted">
            Quarterly priorities. Everyone sees every rock — accountability is public in EOS.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <QuarterPicker value={quarter} onChange={setQuarter} />
          <Button
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <Plus className="size-4" />
            Add rock
          </Button>
        </div>
      </div>

      {/* Rollup strip — the quarterly-session headline numbers. */}
      <div className="grid grid-cols-3 gap-3">
        {(['on_track', 'off_track', 'done'] as const).map((status) => (
          <Card key={status} className="p-4 text-center">
            <p className="text-[22px] font-semibold text-kw-shark">{statusCounts[status]}</p>
            <div className="mt-1 flex justify-center">
              <RockStatusBadge status={status} />
            </div>
          </Card>
        ))}
      </div>

      <Tabs
        tabs={[
          { id: 'company', label: 'Company' },
          { id: 'departments', label: 'Departments' },
          { id: 'mine', label: 'Mine' },
        ]}
        activeTab={tab}
        onChange={(id) => {
          setTab(id as RockTab);
        }}
      />

      {rocksQuery.isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : rocksQuery.error ? (
        <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
          <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
          <div>
            <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load rocks</p>
            <p className="mt-1 text-[13px] text-kw-danger-text/80">
              {rocksQuery.error instanceof Error ? rocksQuery.error.message : 'Unknown error'}
            </p>
          </div>
        </Card>
      ) : visibleRocks.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center">
          <Mountain className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">No rocks here yet</p>
            <p className="mt-1 text-[13px] text-kw-muted">
              {tab === 'mine'
                ? 'Add an individual rock to commit to a quarterly priority.'
                : 'Rocks for this quarter will appear here once they are set.'}
            </p>
          </div>
          <Button
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <Plus className="size-4" />
            Add your first rock
          </Button>
        </Card>
      ) : grouped ? (
        <div className="space-y-6">
          {grouped.map(([label, groupRocks]) => (
            <div key={label} className="space-y-3">
              <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">{label}</h2>
              {groupRocks.map(renderRock)}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">{visibleRocks.map(renderRock)}</div>
      )}

      <CreateRockModal
        key={quarter}
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        onCreated={() => {
          invalidate();
          toast.show('Rock added', { variant: 'success' });
        }}
        defaultQuarter={quarter}
        canManageAll={canManageAll}
        isDeptLead={isDeptLead}
        myMembershipId={myMembershipId}
      />

      {openRock ? (
        <RockDetailDrawer
          key={openRock.id}
          rock={openRock}
          allRocks={rocks}
          canEdit={canEditRock(openRock)}
          onClose={() => {
            setOpenRockId(null);
          }}
        />
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Rock card
// ─────────────────────────────────────────────────────────────────────────

function RockCard({
  rock,
  ownerName,
  departmentLabel,
  canEdit,
  canArchive,
  onCycleStatus,
  onArchive,
  onOpen,
}: {
  rock: Rock;
  ownerName: string | null;
  departmentLabel: string | null;
  canEdit: boolean;
  canArchive: boolean;
  onCycleStatus: () => void;
  onArchive: () => void;
  onOpen: () => void;
}) {
  const doneCount = rock.milestones.filter((m) => m.completedAt !== null).length;

  return (
    <Card className="p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={onOpen}
            className="text-left text-[14px] font-medium text-kw-shark hover:underline"
          >
            {rock.title}
          </button>
          {rock.description ? <p className="mt-0.5 text-[13px] text-kw-muted">{rock.description}</p> : null}
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px] text-kw-muted">
            <span className="capitalize">{rock.type}</span>
            {departmentLabel ? <span>· {departmentLabel}</span> : null}
            {ownerName ? <span>· {ownerName}</span> : null}
            <span>
              · {doneCount}/{rock.milestones.length} milestones
            </span>
            {rock.approvalState === 'pending_approval' ? (
              <Badge variant="warning">Pending approval</Badge>
            ) : rock.approvalState === 'draft' ? (
              <Badge variant="neutral">Draft</Badge>
            ) : null}
            {rock.blocked ? <Badge variant="warning">Blocked</Badge> : null}
            {rock.offTrackMilestoneCount > 0 ? (
              <span className="inline-flex items-center font-medium text-kw-warning-text">
                ⚠ {rock.offTrackMilestoneCount} off track
              </span>
            ) : null}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {canEdit ? (
            <button
              type="button"
              aria-label={`Change status of ${rock.title} (now ${ROCK_STATUS_META[rock.status].label})`}
              title="Click to cycle status"
              onClick={onCycleStatus}
              className="rounded-kw-full transition-opacity hover:opacity-80"
            >
              <RockStatusBadge status={rock.status} />
            </button>
          ) : (
            <RockStatusBadge status={rock.status} />
          )}
          {canArchive ? (
            <button
              type="button"
              aria-label={`Archive ${rock.title}`}
              onClick={onArchive}
              className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-danger-text"
            >
              <Trash2 className="size-3.5" />
            </button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Create rock modal
// ─────────────────────────────────────────────────────────────────────────

/** Friendly copy for the assistant's non-fatal errors (503 ai_disabled is
 *  surfaced separately as a typed AiDisabledError). */
function describeAssistantError(err: unknown): string {
  switch (eosErrorCode(err)) {
    case 'ai_refused':
      return 'The assistant couldn’t draft a rock from that — try a note, or fill the form in yourself.';
    case 'ai_unavailable':
      return 'The assistant is briefly unavailable — try again, or fill the form in yourself.';
    case 'seat_not_found':
      return 'That seat no longer exists — refresh the page.';
    default:
      return 'The assistant couldn’t draft a rock — please fill the form in yourself.';
  }
}

function CreateRockModal({
  open,
  onClose,
  onCreated,
  defaultQuarter,
  canManageAll,
  isDeptLead,
  myMembershipId,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultQuarter: string;
  canManageAll: boolean;
  isDeptLead: boolean;
  myMembershipId: string | null;
}) {
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<RockType>('individual');
  const [ownerMembershipId, setOwnerMembershipId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [quarter, setQuarter] = useState(defaultQuarter);

  // AI assist — drafts a title/description (+ milestone ideas) from an
  // optional note. Degrades quietly to "AI unavailable" when the server
  // reports AI is switched off (503 ai_disabled).
  const [aiNote, setAiNote] = useState('');
  const [aiDisabled, setAiDisabled] = useState(false);
  const [suggestedMilestones, setSuggestedMilestones] = useState<string[]>([]);

  const suggestMutation = useMutation({
    mutationFn: () => suggestRock({ note: aiNote.trim() || undefined }),
    onSuccess: (s) => {
      setTitle(s.title);
      setDescription(s.description);
      setSuggestedMilestones(s.milestones.map((m) => m.title));
    },
    onError: (err) => {
      if (err instanceof AiDisabledError) {
        setAiDisabled(true);
        return;
      }
      toast.show(describeAssistantError(err), { variant: 'danger' });
    },
  });

  // Plain members create individual rocks for themselves only; leads add
  // department rocks; manage_eos/superadmin get the full matrix.
  const typeOptions: RockType[] = canManageAll
    ? ['company', 'department', 'individual']
    : isDeptLead
      ? ['department', 'individual']
      : ['individual'];
  const canPickOwner = canManageAll || isDeptLead;

  const departmentsQuery = useQuery({
    queryKey: ['departments'],
    queryFn: listDepartments,
    enabled: open,
  });
  const employeesQuery = useQuery({
    queryKey: ['employees', 'directory'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
    enabled: open && canPickOwner,
  });
  const members = useMemo(
    () => (employeesQuery.data?.data ?? []).filter((e) => e.membershipId !== null),
    [employeesQuery.data],
  );

  const mutation = useMutation({
    mutationFn: () =>
      createRock({
        title: title.trim(),
        description: description.trim() || undefined,
        type,
        // Plain members: omit → the server defaults individual rocks to self.
        ownerMembershipId: canPickOwner
          ? ownerMembershipId || (type === 'individual' ? (myMembershipId ?? undefined) : undefined)
          : undefined,
        departmentId: type === 'department' && departmentId ? departmentId : undefined,
        quarterStart: quarter,
      }),
    onSuccess: () => {
      onCreated();
      onClose();
      setTitle('');
      setDescription('');
      setType('individual');
      setOwnerMembershipId('');
      setDepartmentId('');
      setQuarter(defaultQuarter);
      setAiNote('');
      setSuggestedMilestones([]);
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    if (!title.trim()) return;
    if (type === 'department' && !departmentId) return;
    mutation.mutate();
  }

  return (
    <Modal open={open} onClose={onClose} title="Add rock" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* AI assist — drafts the title/description from an optional note. */}
        <div className="rounded-kw-md border border-kw-cerulean/20 bg-kw-cerulean-light/30 p-3">
          {aiDisabled ? (
            <p className="flex items-center gap-1.5 text-[12px] text-kw-muted">
              <Sparkles className="size-3.5" />
              AI unavailable — fill the form in below.
            </p>
          ) : (
            <div className="space-y-2">
              <input
                value={aiNote}
                onChange={(e) => {
                  setAiNote(e.target.value);
                }}
                aria-label="What should this rock be about?"
                placeholder="Optional: what should this rock be about?"
                className="w-full rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
              />
              <Button
                type="button"
                variant="secondary"
                size="sm"
                isLoading={suggestMutation.isPending}
                onClick={() => {
                  suggestMutation.mutate();
                }}
              >
                <Sparkles className="size-4" />
                Help me create a Rock
              </Button>
            </div>
          )}
        </div>

        <Input
          label="Title"
          name="rock-title"
          required
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          placeholder="e.g. Launch the new warehouse flow"
        />

        {suggestedMilestones.length > 0 ? (
          <div className="rounded-kw-md border border-kw-border bg-kw-bg/50 p-3">
            <p className="text-[12px] font-medium text-kw-label">Suggested milestones</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[12px] text-kw-muted">
              {suggestedMilestones.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
            <p className="mt-1.5 text-[11px] text-kw-muted">
              Add these from the rock’s drawer once it’s created.
            </p>
          </div>
        ) : null}

        <label className="flex flex-col gap-1">
          <span className="text-[13px] font-medium text-kw-label">Description</span>
          <textarea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            rows={2}
            placeholder="Optional context"
            className="rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          />
        </label>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Type</span>
            {typeOptions.length === 1 ? (
              <p className="rounded-kw-md border border-kw-border bg-kw-bg px-2.5 py-1.5 text-[13px] capitalize text-kw-muted">
                Individual (yours)
              </p>
            ) : (
              <select
                value={type}
                onChange={(e) => {
                  setType(e.target.value as RockType);
                }}
                className={SELECT_CLS}
              >
                {typeOptions.map((t) => (
                  <option key={t} value={t} className="capitalize">
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </option>
                ))}
              </select>
            )}
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Quarter</span>
            <QuarterPicker value={quarter} onChange={setQuarter} />
          </label>
        </div>

        {canPickOwner ? (
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Owner</span>
            <select
              value={ownerMembershipId}
              onChange={(e) => {
                setOwnerMembershipId(e.target.value);
              }}
              className={SELECT_CLS}
            >
              <option value="">{type === 'individual' ? 'Myself' : 'Unassigned'}</option>
              {members.map((m) => (
                <option key={m.id} value={m.membershipId ?? ''}>
                  {m.firstName} {m.lastName}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {type === 'department' ? (
          <label className="flex flex-col gap-1">
            <span className="text-[13px] font-medium text-kw-label">Department</span>
            <select
              value={departmentId}
              onChange={(e) => {
                setDepartmentId(e.target.value);
              }}
              required
              className={SELECT_CLS}
            >
              <option value="">Pick a department…</option>
              {(departmentsQuery.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        {mutation.error ? (
          <div className="flex items-start gap-2 rounded-kw-md border border-kw-danger-text/30 bg-kw-danger-bg p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-kw-danger-text" />
            <p className="text-[12px] text-kw-danger-text">
              {describeEosError(mutation.error, 'Could not add the rock.')}
            </p>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={mutation.isPending}
            disabled={!title.trim() || (type === 'department' && !departmentId)}
          >
            Add rock
          </Button>
        </div>
      </form>
    </Modal>
  );
}
